import React, { useState, useEffect, useCallback } from 'react';
import { connect } from 'react-redux';
import { useQuery } from 'react-query';
import { Form, Modal, Radio, DatePicker } from 'antd-v5';
import dayjs from 'dayjs';
import DropdownSelect from '../../../../../Layout/DropdownSelect';
import { postRequest, getRequest } from '../../../../../../core/apiRequests';
import { notificationError, notificationSuccess } from '../../../../../../core/actions';
import NumberInput from '../../../../../Layout/NumberInput';
import Input from '../../../../../CustomizedAntDesign/Input/Input';
import KioskSlider from '../../../../../Layout/KioskSlider';

const AddReminderModal = props => {
  const [reminderTimeframe, setReminderTimeframe] = useState();
  const [scheduledDate, setScheduledDate] = useState(dayjs());
  const [factor, setFactor] = useState(2);
  const [timeUnit, setTimeUnit] = useState('weeks');
  const [repeats, toggleRepeats] = useState(false);
  const [descriptionText, setDescriptionText] = useState('');
  const [loading, setLoading] = useState(false);
  const [associate, setAssociate] = useState(props.client.associate_id);
  const [openAssociateModal, setOpenAssociateModal] = useState(false);

  useEffect(() => {
    setAssociate(props.client.associate_id);
  }, [props.client.associate_id]);

  const timeframeList = [
    // { label: 'Select Time Frame', value: 0 },
    { label: 'Time Period', value: 'timePeriod' },
    { label: 'Specific Date', value: 'specificDate' },
  ];

  const timeUnitList = [
    { label: 'Days', value: 'days' },
    { label: 'Weeks', value: 'weeks' },
    { label: 'Months', value: 'months' },
  ];

  const { data: allAssociates, isLoading } = useQuery(
    'getAssociates',
    async () => {
      const reqObj = {
        params: ['associates', props.auth.merchantId, 'getAllAssociateList'],
      };
      const data = await getRequest(reqObj);
      return data
        .map(associate => ({
          label: `${associate.first_name || ''} ${associate.last_name || ''}`.trim(),
          value: associate.id,
        }))
        .sort((a, b) => (b.label < a.label ? 1 : -1));
    },
    {
      refetchOnWindowFocus: false,
      initialData: [],
    },
  );

  const saveHandler = () => {
    if (props.auth.merchantId) {
      if (descriptionText) {
        if (reminderTimeframe === 'timePeriod') {
          if (`${factor}`) {
            if (factor >= 0) {
              makeTheCall();
            } else {
              notificationError("Time Interval Can't Be Negative");
            }
          } else {
            notificationError('Please Set A Time Interval');
          }
        }
        if (reminderTimeframe === 'specificDate') {
          if (scheduledDate) {
            makeTheCall();
          } else {
            notificationError('Please Set Scheduled Date');
          }
        }
      } else {
        notificationError('Please Add Reminder Description');
      }
    }
    function makeTheCall() {
      setLoading(true);
      const reqObj = {
        params: ['chatMessages', props.auth.merchantId, 'addReminder'],
        data: {
          clientId: props.clientId,
          createdBy: (`${props.auth.firstName} ` || '') + (props.auth.lastName || '').trim(),
          descriptionText,
          reminderTimeframe,
          factor,
          timeUnit,
          repeats,
          associate,
          scheduledDate,
        },
      };
      (async () => {
        const respData = await postRequest(reqObj);
        if (respData.id) {
          notificationSuccess('Reminder Created');
        } else {
          // notificationError('Error Creating Reminder');
        }
        setLoading(false);
        setTimeout(() => {
          props.setReload(prev => !prev);
        }, 500);
        props.setShowReminderModal(false);
      })();
    }
  };

  useEffect(() => {
    if (associate && descriptionText && reminderTimeframe) {
      setLoading(false);
    }
  }, [associate, descriptionText, reminderTimeframe]);

  const handleSubmit = useCallback(() => {
    if (!props.client.associate_id || props.client.associate_id === props.auth.userId) {
      setAssociate(props.auth.userId);
      saveHandler();
    } else {
      setOpenAssociateModal(true);
    }
  }, [props.client.associate_id, props.auth.userId, saveHandler]);

  return (
    <>
      <Modal
        title="Add Reminder"
        open
        onCancel={() => {
          props.setShowReminderModal(false);
          setReminderTimeframe();
        }}
        onOk={handleSubmit}
        okText="Save"
        okButtonProps={{ disabled: loading }}
        confirmLoading={loading}
        destroyOnClose
        width={404}
        centered
      >
        <div className="align-left m-top-24">
          <span className="dropDownDescriptionText">Description</span>
          <Input
            placeholder="Reminder description..."
            className="h-50 m-top-8 fs-13 outline-none common_inputStyle left-0 input-height"
            style={{ boxSizing: 'border-box', width: '100%' }}
            value={descriptionText}
            onChange={e => setDescriptionText(e.target.value)}
          />
        </div>
        <div className="align-left m-top-24">
          <span className="dropDownDescriptionText">When do you want to be reminded?</span>
          <DropdownSelect
            classProps="w-100-P m-top-8"
            value={reminderTimeframe}
            placeholder="Select Time Frame"
            options={timeframeList}
            onChange={obj => setReminderTimeframe(obj.value)}
          />
        </div>
        <div style={{ minHeight: '125px', marginTop: '22px' }}>
          {reminderTimeframe ? (
            <TimeFrame
              reminderTimeframe={reminderTimeframe}
              toggleRepeats={toggleRepeats}
              scheduledDate={scheduledDate}
              setScheduledDate={setScheduledDate}
              factor={factor}
              setFactor={setFactor}
              timeUnit={timeUnit}
              setTimeUnit={setTimeUnit}
              timeUnitList={timeUnitList}
              repeats={repeats}
            />
          ) : null}
        </div>
      </Modal>
      <Modal
        open={openAssociateModal}
        onCancel={() => {
          setOpenAssociateModal(false);
        }}
        centered
        title="Another associate is assigned to this client"
        onOk={saveHandler}
        okText="Save"
        closable={false}
      >
        <Form.Item className="m-top-16" label="Who should this reminder be assigned to?" layout="vertical">
          <Radio.Group
            onChange={e => setAssociate(e.target.value)}
            value={associate}
            defaultValue={props.client.associate_id}
          >
            <Radio value={props.client.associate_id}>
              {`${props?.client?.associate?.first_name || ''} ${
                props?.client?.associate?.last_name || ''
              } (Assigned associate)`}
            </Radio>
            <Radio value={props.auth.userId}>{`${props?.auth?.firstName || ''} ${
              props?.auth?.lastName || ''
            } (you)`}</Radio>
          </Radio.Group>
        </Form.Item>
      </Modal>
    </>
  );
};
const mapStateToProps = state => ({
  auth: { ...state.auth },
  client: { ...state.client },
});
export default connect(mapStateToProps, {})(AddReminderModal);

const TimeFrame = ({
  reminderTimeframe,
  toggleRepeats,
  scheduledDate,
  setScheduledDate,
  factor,
  setFactor,
  timeUnit,
  setTimeUnit,
  timeUnitList,
  repeats,
}) => {
  if (reminderTimeframe === 'timePeriod') {
    return (
      <div>
        <div className="flex-row-spacebetween-nowrap w-100-P">
          <NumberInput
            value={factor}
            classProps="w-140 h-50 fs-13  left-0 input-height"
            onChange={val => setFactor(val)}
            min={0}
          />
          <DropdownSelect
            classProps=" w-140"
            placeholder="Choose an opportunity..."
            value={timeUnit}
            options={timeUnitList}
            onChange={obj => setTimeUnit(obj.value)}
          />
        </div>
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            textAlign: 'left',
          }}
          className="m-top-11"
        >
          <KioskSlider value={repeats} onChange={() => toggleRepeats(!repeats)} />
          <span className="m-left-10 fw-500 " style={{ marginTop: '2px' }}>
            Repeats
          </span>
        </div>
      </div>
    );
  }
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'left',
        width: '100%',
        textAlign: 'left',
      }}
    >
      <span className="dropDownDescriptionText">Select a Date</span>
      <DatePicker
        value={scheduledDate}
        defaultPickerValue={dayjs()}
        onChange={date => setScheduledDate(date)}
        style={{ width: '100%', marginTop: '8px' }}
      />
    </div>
  );
};
